import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { Credentials } from "storefront/GrailedAPI/v1/Sessions/create";
import type { TwoFactorChallenge } from "storefront/GrailedAPI/NextAction";
import verifySignIn, {
  VerifySignInError,
} from "storefront/GrailedAPI/v1/Sessions/verify";
import resendSignInCode, {
  ResendSignInCodeError,
} from "storefront/GrailedAPI/v1/Sessions/resend";
import {
  Form,
  FormControl,
  FormError,
  FormLabel,
  FormSubmit,
  Input,
} from "storefront/components/FormV2";
import Body from "storefront/components/Body";
import LinkButton from "storefront/components/LinkButton";
import SemiBoldBody from "storefront/components/SemiboldBody";
import { AuthenticationData } from "../Data";
import { Message } from "../Layout";
import styles from "./TwoFactorChallengeForm.module.scss";

const CODE_LENGTH = 6;

type FormValues = {
  code: string;
};

type Props = {
  challenge: TwoFactorChallenge;
  credentials: Credentials;
  onSuccess: (data: AuthenticationData) => void;
  onSignInAgain: () => void;
};

const minutesUntil = (seconds: number): number =>
  Math.max(1, Math.ceil(seconds / 60));

const resendAvailableAtFor = (challenge: TwoFactorChallenge): number =>
  Date.now() + challenge.resendAvailableIn * 1000;

const TwoFactorChallengeForm = ({
  challenge: initialChallenge,
  credentials,
  onSuccess,
  onSignInAgain,
}: Props) => {
  const formMethods = useForm<FormValues>({ defaultValues: { code: "" } });
  const { clearErrors, formState, setError, setFocus, setValue, watch } =
    formMethods;
  const [challenge, setChallenge] = useState(initialChallenge);
  const [mustSignInAgain, setMustSignInAgain] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(() =>
    resendAvailableAtFor(initialChallenge),
  );
  const [now, setNow] = useState(() => Date.now());
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const code = watch("code");
  const errorMessage = formState.errors.code?.message;
  const isLocked = challenge.attemptsRemaining === 0;

  const isCodeEditable = !isLocked && !mustSignInAgain;
  const resendSecondsLeft = Math.max(
    0,
    Math.ceil((resendAvailableAt - now) / 1000),
  );
  const canShowResend = isCodeEditable && challenge.resendsRemaining > 0;
  const canResend =
    canShowResend &&
    resendSecondsLeft === 0 &&
    !isResending &&
    !formState.isSubmitting;

  const updateChallenge = (nextChallenge: TwoFactorChallenge) => {
    setChallenge(nextChallenge);
    setResendAvailableAt(resendAvailableAtFor(nextChallenge));
    setNow(Date.now());
  };

  // Focus would otherwise fall back to the modal itself, both on mount (the sign in form that had
  // focus is gone) and after each attempt (the input is disabled while submitting).
  useEffect(() => {
    if (!formState.isSubmitting && isCodeEditable) setFocus("code");
  }, [formState.isSubmitting, isCodeEditable, setFocus]);

  useEffect(() => {
    if (resendSecondsLeft === 0) return undefined;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [resendSecondsLeft]);

  // Codes are only digits, so stripping everything else keeps a typo from using up an attempt
  // and lets pasted codes like "123 456" or "123-456" work.
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      "code",
      event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH),
    );
    clearErrors("code");
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const data = await verifySignIn({
        challengeToken: challenge.challengeToken,
        code: values.code,
        credentials,
      });
      onSuccess(data);
    } catch (error: unknown) {
      if (!(error instanceof VerifySignInError)) {
        setError("code", {
          message: new VerifySignInError({ type: "Unknown" }).message,
        });
        return;
      }

      switch (error.failure.type) {
        case "IncorrectCode":
        case "ChallengeLocked":
          updateChallenge(error.failure.challenge);
          setValue("code", "");
          break;

        case "SignInAgain":
        case "Banned":
          setMustSignInAgain(true);
          break;

        default:
          break;
      }

      setError("code", { message: error.message });
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setResendMessage("");

    try {
      const nextChallenge = await resendSignInCode(challenge.challengeToken);
      updateChallenge(nextChallenge);
      setValue("code", "");
      clearErrors("code");
      setResendMessage(`We sent a new code to ${nextChallenge.sentTo}.`);
      setFocus("code");
    } catch (error: unknown) {
      if (!(error instanceof ResendSignInCodeError)) {
        setResendMessage(
          new ResendSignInCodeError({ type: "Unknown" }).message,
        );
        return;
      }

      switch (error.failure.type) {
        case "ResendUnavailable":
          updateChallenge(error.failure.challenge);
          break;

        case "SignInAgain":
          setMustSignInAgain(true);
          break;

        default:
          break;
      }

      setResendMessage(error.message);
    } finally {
      setIsResending(false);
    }
  };

  const renderStatus = () => {
    if (isLocked) {
      return (
        <Body className={styles.status}>
          You can sign in again in {minutesUntil(challenge.expiresIn)}{" "}
          {minutesUntil(challenge.expiresIn) === 1 ? "minute" : "minutes"}.
        </Body>
      );
    }

    if (errorMessage && !mustSignInAgain) {
      return (
        <Body className={styles.status}>
          {challenge.attemptsRemaining}{" "}
          {challenge.attemptsRemaining === 1 ? "attempt" : "attempts"} remaining
        </Body>
      );
    }

    return null;
  };

  return (
    <Form
      className={styles.twoFactorChallengeForm}
      formMethods={formMethods}
      onSubmit={handleSubmit}
    >
      <SemiBoldBody className={styles.title}>Check your email</SemiBoldBody>

      <Body className={styles.note}>
        For your security, enter the {CODE_LENGTH}-digit code we sent to{" "}
        {challenge.sentTo} to finish signing in.
      </Body>

      <FormControl className={styles.code} name="code">
        <FormLabel>Verification code</FormLabel>
        <Input
          autoComplete="one-time-code"
          inputMode="numeric"
          disabled={!isCodeEditable}
          rules={{ onChange: handleCodeChange }}
        />
        <div role="alert">
          <FormError />
          {renderStatus()}
        </div>
      </FormControl>

      <FormSubmit
        className={styles.submit}
        disabled={code.length !== CODE_LENGTH || !isCodeEditable || isResending}
      >
        Verify
      </FormSubmit>

      {canShowResend ? (
        <div className={styles.resendRow}>
          <Message>
            Didn&apos;t get a code?{" "}
            <LinkButton
              className={styles.resend}
              variant="underline"
              onClick={handleResend}
              disabled={!canResend}
            >
              {resendSecondsLeft > 0
                ? `Resend in ${resendSecondsLeft}s`
                : "Resend code"}
            </LinkButton>
          </Message>
        </div>
      ) : null}

      <div role="status">
        {resendMessage ? (
          <Body className={styles.resendStatus}>{resendMessage}</Body>
        ) : null}
      </div>

      <div className={styles.signInAgain}>
        <Message>
          <LinkButton variant="underline" onClick={onSignInAgain}>
            Back
          </LinkButton>
        </Message>
      </div>
    </Form>
  );
};

export default TwoFactorChallengeForm;
